// src/components/ui/AsignarOfertaModal.jsx
import React, { useMemo, useState } from 'react';

const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE = resolveApiBase();

export default function AsignarOfertaModal({ producto, setModalVisible }) {
  const { id, precio } = producto || {};
  const [tipoOferta, setTipoOferta]   = useState('agotarExistencia'); // 'agotarExistencia' | 'dias'
  const [precioOferta, setPrecioOferta] = useState(precio ?? 0);
  const [diasOferta, setDiasOferta]   = useState(0);
  const [descripcion, setDescripcion] = useState('');
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState('');

  const precioActualFmt = useMemo(
    () => `Q ${Number(precio ?? 0).toFixed(2)}`,
    [precio]
  );

  const validar = () => {
    setErr('');
    const p = Number(precioOferta);
    if (Number.isNaN(p) || p < 0) return 'Precio de oferta inválido';
    if (p === 0) return 'El precio de oferta no puede ser 0';
    if (tipoOferta === 'dias') {
      const d = Number(diasOferta);
      if (!Number.isInteger(d) || d <= 0) return 'Ingresa la cantidad de días (> 0)';
    }
    return '';
  };

  const asignarOferta = async () => {
    if (saving) return;
    const v = validar();
    if (v) { setErr(v); return; }

    setSaving(true);
    setErr('');

    // El backend ya calcula fechaInicio/fechaFin; igual mandamos diasOferta si aplica
    const payload = {
      productoId: id,
      tipoOferta,
      precioOferta: Number(precioOferta),
      diasOferta: tipoOferta === 'dias' ? Number(diasOferta) : null,
      descripcion: descripcion || null,
    };

    // Fallback timeout “blando” por si algún proxy/antivirus interfiere
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      setSaving(false);
      //alert('La solicitud está tardando. Es posible que la oferta se haya guardado. Verifica la lista de ofertas.');
      setModalVisible(false);
    }, 200); // 20s

    try {
      const res = await fetch(`${API_BASE}/api/ofertas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      // si ya cayó el timeout soft, no hacemos nada más para evitar dobles alerts
      if (timedOut) return;

      clearTimeout(timer);

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.msg || res.statusText);

      setSaving(false);
      alert('✅ Oferta asignada correctamente');
      setModalVisible(false);
    } catch (e) {
      if (!timedOut) {
        clearTimeout(timer);
        setSaving(false);
        setErr(String(e.message || e));
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6">
        <header className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold">Asignar oferta</h3>
            <p className="text-sm text-slate-500">Producto #{id}</p>
          </div>
          <button
            onClick={() => setModalVisible(false)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
            disabled={saving}
          >
            Cerrar
          </button>
        </header>

        <div className="mt-4 space-y-4">
          {/* Tipo de oferta */}
          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">Tipo de oferta</div>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={`cursor-pointer rounded-xl border p-3 text-sm ${
                  tipoOferta === 'agotarExistencia'
                    ? 'border-sky-600 bg-sky-50'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="tipo"
                  className="mr-2 align-middle"
                  checked={tipoOferta === 'agotarExistencia'}
                  onChange={() => setTipoOferta('agotarExistencia')}
                  disabled={saving}
                />
                Hasta agotar existencia
              </label>

              <label
                className={`cursor-pointer rounded-xl border p-3 text-sm ${
                  tipoOferta === 'dias'
                    ? 'border-sky-600 bg-sky-50'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="tipo"
                  className="mr-2 align-middle"
                  checked={tipoOferta === 'dias'}
                  onChange={() => setTipoOferta('dias')}
                  disabled={saving}
                />
                Cantidad de días
              </label>
            </div>
          </div>

          {tipoOferta === 'dias' && (
            <div>
              <label className="block text-sm font-medium text-slate-700">Cantidad de días</label>
              <input
                type="number"
                min={1}
                value={diasOferta}
                onChange={(e) => setDiasOferta(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                disabled={saving}
              />
            </div>
          )}

          {/* Precios */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Precio actual</div>
              <div className="mt-1 text-lg font-semibold">{precioActualFmt}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Precio de oferta</label>
              <div className="mt-1 flex rounded-lg border border-slate-300 overflow-hidden">
                <span className="px-3 py-2 text-slate-500 text-sm border-r border-slate-200">Q</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={precioOferta}
                  onChange={(e) => setPrecioOferta(e.target.value)}
                  className="flex-1 px-3 py-2 outline-none"
                  disabled={saving}
                />
              </div>
            </div>
          </div>

          {/* Descripción de oferta */}
          <div>
            <label className="block text-sm font-medium text-slate-700">Descripción de oferta</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              placeholder="Descripción de oferta"
              disabled={saving}
            />
          </div>

          {/* Error */}
          {err && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2">
              {err}
            </div>
          )}
        </div>

        <footer className="mt-6 flex justify-end gap-2">
          <button
            onClick={() => setModalVisible(false)}
            className="px-4 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            onClick={asignarOferta}
            className="px-4 py-2 rounded-xl bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-60"
            disabled={saving}
          >
            {saving ? 'Guardando…' : 'Asignar oferta'}
          </button>
        </footer>
      </div>
    </div>
  );
}
